import { ErrorCodeEnum } from '@ulysses-inc/harami_api_client'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

/**
 * キオスク端末（kiosk）で認証・認可に失敗したときに表示するエラー画面の種別。
 * Rump のガイドライン「キオスク端末アカウントのログイン・ログアウトの表現統一」の区分に対応する。
 *
 * - device_setup: ① 証明書および接続自体に問題がある場合（証明書なし・トークン取得失敗・セッション未確立など）。
 *   文言は全サービス共通で固定。
 * - service_assignment_required: ② 端末アカウントにカミナシ レポートの利用サービスが割り当てられていない
 *   （ServiceAssignmentRequiredError）。証明書は正常で通信もできるが、カミナシ ID 管理側の設定が必要。
 * - invalid_audience: ② 端末アカウントのトークン設定（audience）に問題がある（InvalidAudienceError）。
 */
export type KioskAuthErrorKind =
  | 'device_setup'
  | 'service_assignment_required'
  | 'invalid_audience'

export type KioskAuthError = {
  kind: KioskAuthErrorKind
  /**
   * 記録中に認証切れになり、編集中のレポートを端末（IndexedDB）へ保存してからこの画面に来た場合 true。
   * 従来は /login のクエリ additionalMessage=offline_saved で Snackbar に出していたが、
   * kiosk では画面遷移せず全画面 Dialog を被せるため、Dialog 内の 1 行として表示する。
   */
  reportSavedOffline: boolean
}

type State = {
  /**
   * kiosk の認証エラー画面を表示中なら値が入る。null なら非表示。
   *
   * persist しない（メモリのみ）。リロードすると ProtectedRoute の認証フローが再実行され、
   * 失敗すれば再検知されるため永続化は不要。
   */
  error: KioskAuthError | null
}

type Actions = {
  /** 認証・認可エラーを検知したときにコールされる。表示中なら上書きする */
  kioskAuthErrorDetected: (
    kind: KioskAuthErrorKind,
    options?: { reportSavedOffline?: boolean },
  ) => void
  /** 再試行が成功した等でエラー画面を閉じるときにコールされる */
  kioskAuthErrorResolved: () => void
}

export const useKioskAuthErrorStore = create<State & Actions>()(
  devtools(
    immer(set => ({
      error: null,
      kioskAuthErrorDetected: (kind, options) =>
        set(state => {
          state.error = {
            kind,
            reportSavedOffline: options?.reportSavedOffline ?? false,
          }
        }),
      kioskAuthErrorResolved: () =>
        set(state => {
          state.error = null
        }),
    })),
  ),
)

export const selectKioskAuthError = (state: State) => state.error

/**
 * API のエラーコードからエラー画面の種別を決める。
 * 21 / 22 は「証明書は正常だが端末アカウントの設定に問題がある」ケース（ガイドライン②）、
 * それ以外（AuthenticationExpired / CredentialsNotFound / AccessTokenInactive / 未指定）は
 * セッションを確立できていない状態として ①（端末の設定が確認できない）に丸める。
 */
export const kioskAuthErrorKindFromErrorCode = (
  errorCode: ErrorCodeEnum | undefined,
): KioskAuthErrorKind => {
  switch (errorCode) {
    case ErrorCodeEnum.ServiceAssignmentRequiredError:
      return 'service_assignment_required'
    case ErrorCodeEnum.InvalidAudienceError:
      return 'invalid_audience'
    default:
      return 'device_setup'
  }
}
